import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
const SLIDE_DURATION = 6000;

// Shown until an admin uploads real cow photos/videos via Manage Gallery
// (category "cows") — keeps the slider/arrows functional out of the box.
const FALLBACK_SLIDES = [
  { _id: 'fallback-1', kind: 'fallback', variant: 1, icon: '', captionKey: 'home.fallbackSlide1' },
  { _id: 'fallback-2', kind: 'fallback', variant: 2, icon: '🙏', captionKey: 'home.fallbackSlide2' },
  { _id: 'fallback-3', kind: 'fallback', variant: 3, icon: '🌾', captionKey: 'home.fallbackSlide3' },
];

export default function HeroSlider({ children }) {
  const { t } = useTranslation();
  const [bannerSlides, setBannerSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const slides = bannerSlides.length > 0 ? bannerSlides : FALLBACK_SLIDES;

  useEffect(() => {
    Promise.all([
      API.get('/gallery?category=cows&placement=banner').then(r => r.data || []).catch(() => []),
      API.get('/videos?placement=banner').then(r => r.data || []).catch(() => []),
    ]).then(([galleryItems, videos]) => {
      const fromGallery = galleryItems.map(g => ({
        _id: g._id,
        kind: g.type === 'video' ? 'galleryVideo' : 'image',
        url: g.url,
        title: g.title,
      }));
      const fromVideos = videos.map(v => ({
        _id: v._id,
        kind: v.sourceType === 'upload' ? 'uploadVideo' : 'youtube',
        url: v.url,
        videoId: v.videoId,
        title: v.title,
      }));
      setBannerSlides([...fromGallery, ...fromVideos]);
    });
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length < 2) return;
    timerRef.current = setTimeout(() => setIndex(i => (i + 1) % slides.length), SLIDE_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [slides.length, index]);

  const getUrl = item => (item.url?.startsWith('http') ? item.url : `${API_BASE}${item.url}`);
  const prev = () => setIndex(i => (i - 1 + slides.length) % slides.length);
  const next = () => setIndex(i => (i + 1) % slides.length);

  // Shortest circular distance from the active slide, used to slide each
  // frame in from the correct side (right for "next", left for "prev")
  const positionClass = (i) => {
    if (i === index) return 'active';
    let delta = i - index;
    const n = slides.length;
    if (delta > n / 2) delta -= n;
    if (delta < -n / 2) delta += n;
    return delta < 0 ? 'behind' : '';
  };

  return (
    <section className="hero">
      <div className="hero-slides">
        {slides.map((s, i) => (
          <div key={s._id} className={`hero-slide ${positionClass(i)}`}>
            {s.kind === 'galleryVideo' || s.kind === 'uploadVideo' ? (
              <video src={getUrl(s)} autoPlay muted loop playsInline />
            ) : s.kind === 'youtube' ? (
              <iframe
                src={`https://www.youtube.com/embed/${s.videoId}?autoplay=1&mute=1&loop=1&playlist=${s.videoId}&controls=0`}
                title={s.title || 'Goshala video'}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                className="hero-slide-youtube"
              />
            ) : s.kind === 'fallback' ? (
              <div className={`hero-slide-fallback hero-slide-fallback-${s.variant}`}>
                <span className="hero-slide-fallback-icon">{s.icon}</span>
                <span className="hero-slide-fallback-caption">{t(s.captionKey)}</span>
              </div>
            ) : (
              <img src={getUrl(s)} alt={s.title || 'Goshala cow'} />
            )}
          </div>
        ))}
        <div className="hero-slide-overlay" />
      </div>

      {slides.length > 1 && (
        <>
          <button type="button" className="hero-slide-arrow hero-slide-arrow-left" onClick={prev} aria-label="Previous slide">‹</button>
          <button type="button" className="hero-slide-arrow hero-slide-arrow-right" onClick={next} aria-label="Next slide">›</button>
          <div className="hero-slide-dots">
            {slides.map((_, i) => (
              <button
                type="button"
                key={i}
                className={`hero-slide-dot${i === index ? ' active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}

      <div className="container hero-inner">{children}</div>
    </section>
  );
}
